import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { roundMoney, toCents } from '@/lib/money';
import { notify } from '@/lib/notifications';
import { logUiAudit } from '@/lib/audit/uiAudit';
import { getDateStrSAST } from '@/lib/sastTime';
import { isFinanceDocumentTypeAllowed, type NormalizedFinanceTierRestrictions } from '@/lib/finance/tierAccess';
import {
  invoiceUnifiedPreviewBlocked,
} from '@/lib/finance/invoiceListUi';
import {
  type DocumentApprovalEvent,
  determineApprovalAction,
  emitDocumentApprovalEvent,
} from '../../types/documentApprovalEvents';
import {
  getErrorMessage,
  hasMeaningfulLineItemContent,
  buildInvoicePreviewLockProbe,
} from '../documentModalHelpers';
import type { DocumentConfig, DocumentFormState } from '../documentModalHelpers';
import type { Document, DocumentType, DocumentLineItem, DocumentStatus } from '@/hooks/useDocumentsWorkItems';
import type { DocumentPhotoAttachment } from '../documentModalHelpers';
import { getDinLineLinkGaps } from '@/lib/finance/expenseAccountMapping';
import { recordVendorMemory } from '@/lib/finance/vendorMemoryClient';
import { emitGuidedTourTrainingEvent } from '@/components/training/guided-tour';

interface RecurringSettings {
  frequency: 'daily' | 'weekly' | 'bi-weekly' | 'monthly' | 'quarterly' | 'yearly';
  start_date: string;
  end_date: string;
  day_of_month: number;
  day_of_week: number;
  auto_send: boolean;
  auto_approve: boolean;
  generate_days_before: number;
}

interface PersistDocumentOptions {
  shouldSend?: boolean;
  closeAfterSave?: boolean;
  openShareOnSave?: boolean;
  skipOnSaved?: boolean;
}

interface UseDocumentLifecycleParams {
  isOpen: boolean;
  doc_type: string;
  document: Document | null | undefined;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  items: DocumentLineItem[];
  itemsLoadedRef: React.MutableRefObject<boolean>;
  photoAttachments: DocumentPhotoAttachment[];
  config: DocumentConfig;
  isPurchaseDocument: boolean;
  hasDocumentContact: boolean;
  isBlankUnsavedDraft: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  linkedParentDocument: Document | null;
  importedWorkItemIds: string[];
  importedTimeEntryIds: string[];
  setImportedWorkItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setImportedTimeEntryIds: React.Dispatch<React.SetStateAction<string[]>>;
  syncPhotoAttachments: (workItemId: string, persistedVia: 'auto_save' | 'manual_save') => Promise<{ uploadedCount: number; updatedCount: number; failedCount: number }>;
  createDocument: (data: Partial<Document>) => Promise<Document | null>;
  updateDocument: (id: string, data: Partial<Document>) => Promise<Document | null>;
  deleteDocument: (id: string) => Promise<boolean>;
  onClose: () => void;
  onSaved?: (doc: Document) => void;
  onConverted?: (result: { success: boolean; document?: Record<string, unknown>; targetType?: DocumentType }) => void;
  financeTierRestrictions: NormalizedFinanceTierRestrictions | null | undefined;
  isDarkMode: boolean;
  unifiedReportDocumentType: string | null;
  /** Client-level inventory movement tracking (custom_settings.inventory_tracking_enabled). */
  inventoryTrackingEnabled?: boolean;
}

export function useDocumentLifecycle({
  isOpen,
  doc_type,
  document: doc,
  formData,
  setFormData,
  items,
  itemsLoadedRef,
  photoAttachments,
  config,
  isPurchaseDocument,
  hasDocumentContact,
  isBlankUnsavedDraft,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  linkedParentDocument,
  importedWorkItemIds,
  importedTimeEntryIds,
  setImportedWorkItemIds,
  setImportedTimeEntryIds,
  syncPhotoAttachments,
  createDocument,
  updateDocument,
  deleteDocument,
  onClose,
  onSaved,
  onConverted,
  financeTierRestrictions,
  isDarkMode,
  inventoryTrackingEnabled = true,
  unifiedReportDocumentType,
}: UseDocumentLifecycleParams) {
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Auto-save state
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hasUnsavedChangesRef = useRef(false);

  // Keep ref in sync with state so async callbacks read the current value
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Share / send state
  const [showShareModal, setShowShareModal] = useState(false);
  const [savedDocument, setSavedDocument] = useState<Document | null>(null);
  const [docShareUrl, setDocShareUrl] = useState<string | null>(null);
  const [docShareUrlLoading, setDocShareUrlLoading] = useState(false);

  // Promotion state
  const [showPromotionMenu, setShowPromotionMenu] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  // Synchronous ref guard — prevents double-materialize from rapid double-clicks
  // before React state commits. The idempotency key on the API call is the
  // server-side safety net, but this avoids wasting a network round-trip.
  const isPromotingRef = useRef(false);

  // Preview overlay state
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showUnifiedPreview, setShowUnifiedPreview] = useState(false);
  const [previewDocumentForUnified, setPreviewDocumentForUnified] = useState<Document | null>(doc || null);

  // Recurring state
  const [createAsRecurring, setCreateAsRecurring] = useState(false);
  const [recurringSettings, setRecurringSettings] = useState<RecurringSettings>({
    frequency: 'monthly',
    start_date: getDateStrSAST(new Date()),
    end_date: '',
    day_of_month: 1,
    day_of_week: 1,
    auto_send: false,
    auto_approve: false,
    generate_days_before: 7,
  });

  // ============================================================================
  // AUTO-SAVE
  // ============================================================================

  useEffect(() => {
    setHasUnsavedChanges(true);

    if (!autoSaveEnabled) return;

    if (doc_type === 'din') return;

    const hasContent = items.length > 0 || formData.contact_id || formData.notes || photoAttachments.length > 0;
    if (!hasContent) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!hasUnsavedChangesRef.current) return;

      // Skip auto-save if editing an existing document but line items
      // haven't been loaded from the server yet — sending empty items
      // would wipe existing line items in the database.
      if (doc?.id && !itemsLoadedRef.current) return;

      if (isSavingRef.current) {
        return;
      }
      isSavingRef.current = true;

      setAutoSaveStatus('saving');

      try {
        const documentData = {
          ...formData,
          items,
          parent_document_id: linkedParentDocument?.id || formData.parent_document_id,
          status: formData.status || 'draft'
        };

        let result: Document | null = null;
        if (doc?.id) {
          result = await updateDocument(doc.id, documentData);
        } else if (formData.id) {
          result = await updateDocument(formData.id, documentData);
        } else {
          result = await createDocument(documentData);
          if (result?.id) {
            setFormData(prev => ({ ...prev, id: result!.id }));
          }
        }

        if (result) {
          const attachmentSyncResult = result.id
            ? await syncPhotoAttachments(String(result.id), 'auto_save')
            : { uploadedCount: 0, updatedCount: 0, failedCount: 0 };
          const hasAttachmentSyncFailures = attachmentSyncResult.failedCount > 0;

          setHasUnsavedChanges(hasAttachmentSyncFailures);
          setAutoSaveStatus(hasAttachmentSyncFailures ? 'error' : 'saved');
          setLastAutoSave(new Date());

          setTimeout(() => {
            setAutoSaveStatus('idle');
          }, hasAttachmentSyncFailures ? 3000 : 2000);
        }
      } catch (err) {
        console.error('Auto-save failed:', err);
        setAutoSaveStatus('error');
        setTimeout(() => {
          setAutoSaveStatus('idle');
        }, 3000);
      } finally {
        isSavingRef.current = false;
      }
    }, 3000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, items, photoAttachments, autoSaveEnabled, linkedParentDocument, doc?.id, syncPhotoAttachments, doc_type]);

  // ============================================================================
  // PROMOTION MENU CLICK-OUTSIDE
  // ============================================================================

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (showPromotionMenu) {
        const target = e.target as HTMLElement | null;
        if (!target || !target.closest('[data-promotion-menu]')) {
          setShowPromotionMenu(false);
        }
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    window.document.addEventListener('touchstart', handleClickOutside);
    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
      window.document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPromotionMenu]);

  useEffect(() => {
    if (!isOpen) {
      setShowPromotionMenu(false);
    }
  }, [isOpen]);

  // ============================================================================
  // CLOSE / CLEANUP
  // ============================================================================

  const handleCloseModal = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    const autoSavedId = formData.id;
    const wasAutoSaved = !!autoSavedId && !doc?.id;
    const hasLineItems = items.some(hasMeaningfulLineItemContent);
    const hasAttachmentContent = photoAttachments.length > 0;
    const hasSupportingContent = Boolean(
      formData.contact_id
      || formData.contact_name?.trim()
      || formData.notes?.trim()
      || formData.internal_notes?.trim()
      || formData.reference?.trim()
    );

    if (wasAutoSaved && !hasLineItems && !hasAttachmentContent && !hasSupportingContent) {
      try {
        if (formData.client_id && autoSavedId) {
          try {
            const token = authService.getTokens()?.access_token;
            if (token) {
              await fetch('/api/v1/inventory/stock-reservations', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  action: 'cancel',
                  client_id: formData.client_id,
                  document_type: doc_type,
                  document_id: autoSavedId,
                  reason: 'Auto-saved draft deleted'
                })
              });
            }
          } catch (reservationError) {
            console.warn('[DocumentModal] Failed to cancel stock reservations for auto-saved draft:', reservationError);
          }
        }

        await deleteDocument(autoSavedId);
      } catch (err) {
        console.warn('Failed to clean up blank auto-saved draft:', err);
      }
    }

    onClose();
  }, [doc_type, formData.client_id, formData.contact_id, formData.contact_name, formData.id, formData.internal_notes, formData.notes, formData.reference, doc?.id, items, photoAttachments.length, deleteDocument, onClose]);

  // ============================================================================
  // CONTENT VALIDATION
  // ============================================================================

  const ensureDocumentHasContentForAction = useCallback((actionLabel: 'save' | 'preview' | 'send') => {
    if (!isBlankUnsavedDraft) {
      return true;
    }

    const actionMessage = actionLabel === 'preview'
      ? 'open print preview'
      : actionLabel === 'send'
        ? 'send this document'
        : 'save this document';

    toast.error(`This ${config.labels.title.toLowerCase()} is blank. Add a customer or supplier, line items, or notes before you ${actionMessage}.`);
    return false;
  }, [config.labels.title, isBlankUnsavedDraft]);

  // ============================================================================
  // MARK WORK ITEMS AS BILLED
  // ============================================================================

  const markWorkItemsAsBilled = useCallback(async (invoiceId: string) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      await fetch('/api/v1/finance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'markWorkItemsBilled',
          invoiceId,
          workItemIds: importedWorkItemIds,
          timeEntryIds: importedTimeEntryIds,
        })
      });

      setImportedWorkItemIds([]);
      setImportedTimeEntryIds([]);
    } catch (error) {
      console.error('Error marking work items as billed:', error);
    }
  }, [importedWorkItemIds, importedTimeEntryIds, setImportedWorkItemIds, setImportedTimeEntryIds]);

  // ============================================================================
  // PERSIST DOCUMENT (core save logic)
  // ============================================================================

  const persistDocument = useCallback(async ({
    shouldSend = false,
    closeAfterSave = false,
    openShareOnSave = shouldSend,
    skipOnSaved = false,
  }: PersistDocumentOptions = {}): Promise<Document | null> => {
    // DINs use the document-review API, not the standard finance document API
    if (doc_type === 'din') {
      const dinId = doc?.id || formData.id;
      if (!dinId) {
        toast.error('DIN ID not found');
        return null;
      }
      const token = authService.getTokens()?.access_token;
      if (!token) {
        toast.error('Not authenticated');
        return null;
      }
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        const editedPayload: Record<string, unknown> = {};
        if (formData.contact_id) editedPayload.linkage_contact_id = formData.contact_id;
        if (formData.notes) editedPayload.notes = formData.notes;
        if (formData.reference) editedPayload.reference = formData.reference;
        if (formData.issued_at) editedPayload.date = formData.issued_at;
        // Slip datetime from the datetime-local field — the real transaction
        // date+time used for duplicate detection (edited_payload.date is the
        // intake date for DINs, not the slip date).
        if (formData.transaction_date) editedPayload.transaction_date = formData.transaction_date;
        if (formData.currency) editedPayload.currency = formData.currency;
        if (formData.grand_total != null) {
          editedPayload.amount = roundMoney(formData.grand_total);
          editedPayload.amount_cents = toCents(formData.grand_total);
        }
        if (formData.contact_name) editedPayload.vendor_name = formData.contact_name;
        if (items.length > 0) editedPayload.line_items = items;

        const isDraftSave = String(formData.status || '').toLowerCase() === 'draft';
        const res = await fetch('/api/v1/finance/document-review', {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: dinId,
            action: isDraftSave ? 'save_draft' : 'save_edit',
            edited_payload: editedPayload,
            matched_contact_id: formData.contact_id ?? undefined,
          }),
        });
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || 'Failed to save DIN');
        }
        const json = await res.json().catch(() => ({}));
        setHasUnsavedChanges(false);
        if (isDraftSave) {
          toast.success('DIN saved as draft');
        } else {
          const dupCheck = json?.data?.duplicateCheck;
          if (dupCheck?.hasDuplicates) {
            notify.warning(`DIN saved — ${dupCheck.duplicates.length} potential duplicate(s) detected for this supplier. Review before promoting.`);
          } else {
            toast.success('DIN review saved');
          }
        }
        emitGuidedTourTrainingEvent({ type: 'document_create' });

        // ── AI Learning loop: record vendor memory for DIN corrections ──
        // DINs are the primary intake path — user corrections to GL account
        // and supplier here are the most valuable learning signal. Without
        // this, the AI keeps suggesting the same wrong GL every time.
        if (formData.contact_name) {
          const vendorName = formData.contact_name;
          const workItemId = String(dinId);

          // Record supplier mapping (vendor → contact_id)
          if (formData.contact_id) {
            recordVendorMemory({
              vendor_name: vendorName,
              contact_id: formData.contact_id,
              contact_name: formData.contact_name,
              work_item_id: workItemId,
            });
          }

          // Record vendor-level GL mapping (overall document categorization)
          const overallAccountCode = formData.items?.[0]?.expense_account_code
            ?? formData.items?.[0]?.account_code
            ?? items?.[0]?.expense_account_code
            ?? items?.[0]?.account_code
            ?? undefined;
          if (overallAccountCode) {
            recordVendorMemory({
              vendor_name: vendorName,
              account_code: overallAccountCode,
              work_item_id: workItemId,
            });
          }

          // Record per-line-item GL corrections
          for (const item of items) {
            const lineAccountCode = item.expense_account_code ?? item.account_code;
            const lineDescription = item.description || item.item_name;
            if (lineAccountCode && lineDescription) {
              recordVendorMemory({
                vendor_name: vendorName,
                account_code: lineAccountCode,
                line_item_description: lineDescription,
                work_item_id: workItemId,
              });
            }
          }
        }

        if (!skipOnSaved && doc) onSaved?.(doc);
        if (closeAfterSave) onClose();
        return doc ?? null;
      } catch (err: unknown) {
        console.error('Error saving DIN:', err);
        toast.error(getErrorMessage(err, 'Failed to save DIN'));
        return null;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    }

    if (!ensureDocumentHasContentForAction(shouldSend ? 'send' : 'save')) {
      return null;
    }

    if (isPurchaseDocument && !hasDocumentContact) {
      toast.error('A supplier must be selected before you can save or send this document.');
      return null;
    }

    if (isSavingRef.current) {
      console.log('[DocumentModal] Prevented double submission');
      return null;
    }

    const isDraftSave = !shouldSend && String(formData.status || '').toLowerCase() === 'draft';
    if (formData.contact_id && !isDraftSave) {
      const { warnIfContactAccountIssue } = await import('@/lib/contacts/accountWarning');
      const proceed = await warnIfContactAccountIssue({
        contactId: formData.contact_id,
        action: shouldSend ? `send this ${doc_type.replace(/_/g, ' ')}` : `save this ${doc_type.replace(/_/g, ' ')}`,
        documentType: doc_type,
        documentTotal: Number(formData.grand_total || 0),
      });
      if (!proceed) return null;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    try {
      // When editing an existing document whose line items haven't been
      // loaded from the server yet, omit items from the payload so the
      // PUT endpoint keeps existing line items intact.
      const skipItems = (doc?.id || formData.id) && !itemsLoadedRef.current && items.length === 0;

      const documentData = {
        ...formData,
        items: skipItems ? undefined : items,
        parent_document_id: linkedParentDocument?.id || formData.parent_document_id,
        source_work_item_ids: importedWorkItemIds.length > 0 ? importedWorkItemIds : undefined,
        source_time_entry_ids: importedTimeEntryIds.length > 0 ? importedTimeEntryIds : undefined,
      };

      const previousStatus = doc?.status || formData.status;

      let result;
      if (doc?.id) {
        result = await updateDocument(doc.id, documentData);
      } else if (formData.id) {
        result = await updateDocument(formData.id, documentData);
      } else {
        result = await createDocument(documentData);
        if (result?.id) {
          setFormData(prev => ({ ...prev, id: result!.id }));
        }
      }

      if (result) {
        const newStatus = result.status;
        if (previousStatus !== newStatus) {
          const action = determineApprovalAction(previousStatus, newStatus);

          if (action !== 'status_changed') {
            const approvalEvent: DocumentApprovalEvent = {
              document_id: String(result.id),
              doc_type: doc_type,
              doc_number: (result as { doc_number?: string }).doc_number || (result as { item_number?: string }).item_number || '',
              action,
              status_before: previousStatus,
              status_after: newStatus,
              total_amount: result.grand_total,
              currency: result.currency || 'ZAR',
            };

            emitDocumentApprovalEvent(approvalEvent);
          }
        }
        const attachmentSyncResult = result.id
          ? await syncPhotoAttachments(String(result.id), 'manual_save')
          : { uploadedCount: 0, updatedCount: 0, failedCount: 0 };
        const hasAttachmentSyncFailures = attachmentSyncResult.failedCount > 0;

        void logUiAudit({
          component: 'DocumentModal',
          action: shouldSend ? 'save_and_send' : 'save',
          moduleKey: 'total-finance',
          entityType: doc_type,
          entityId: result.id,
          details: `${shouldSend ? 'Sent' : 'Saved'} ${doc_type} ${(result as { doc_number?: string }).doc_number || (result as { item_number?: string }).item_number || result.id}`,
          workItemId: result.id ? String(result.id) : undefined,
          metadata: {
            doc_type,
            should_send: shouldSend,
            grand_total: formData.grand_total,
          },
        });
        setPreviewDocumentForUnified(result);
        setHasUnsavedChanges(hasAttachmentSyncFailures);
        setAutoSaveStatus(hasAttachmentSyncFailures ? 'error' : 'saved');
        setLastAutoSave(new Date());

        if (hasAttachmentSyncFailures) {
          toast.error(`Document saved, but ${attachmentSyncResult.failedCount} attachment${attachmentSyncResult.failedCount === 1 ? '' : 's'} still need attention.`);
        }

        if ((doc_type === 'invoice' || doc_type === 'expense') &&
          formData.status !== 'draft' &&
          (importedWorkItemIds.length > 0 || importedTimeEntryIds.length > 0)) {
          await markWorkItemsAsBilled(result.id);
        }

        // Create stock reservations for sales documents with inventory items
        if (!isPurchaseDocument && ['invoice', 'sales_order', 'quote', 'delivery_note'].includes(doc_type)) {
          try {
            const inventoryItems = items.filter(item => item.item_id && item.quantity > 0);
            if (inventoryItems.length > 0 && formData.warehouse_id) {
              const token = authService.getTokens()?.access_token;
              if (token) {
                const reservationResponse = await fetch('/api/v1/inventory/stock-reservations', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    action: 'create',
                    reservations: inventoryItems.map(item => ({
                      work_item_id: String(item.item_id),
                      document_id: result.id,
                      document_type: doc_type,
                      requested_quantity: item.quantity,
                      warehouse_id: formData.warehouse_id,
                    }))
                  })
                });

                if (reservationResponse.ok) {
                  const reservationData = await reservationResponse.json();
                  if (reservationData.success) {
                    console.log(`[DocumentModal] Created ${inventoryItems.length} stock reservations for ${doc_type} ${result.id}`);
                  }
                } else {
                  console.warn('[DocumentModal] Failed to create stock reservations:', await reservationResponse.text());
                }
              }
            }
          } catch (error) {
            console.error('[DocumentModal] Error creating stock reservations:', error);
          }
        }

        // Create inventory movements for finalized documents
        const isFinalized = formData.status !== 'draft';
        if (isFinalized && formData.warehouse_id) {
          try {
            const inventoryItems = items.filter(item => item.item_id && item.quantity > 0);
            if (inventoryItems.length > 0) {
              const token = authService.getTokens()?.access_token;
              if (token) {
                const movementResponse = await fetch('/api/v1/inventory/inventory-movements', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    action: 'create',
                    client_id: formData.client_id,
                    document_id: result.id,
                    document_type: doc_type,
                    document_number: result.doc_number || result.item_number || '',
                    warehouse_id: formData.warehouse_id,
                    line_items: inventoryItems.map(item => ({
                      work_item_id: String(item.item_id),
                      quantity: item.quantity,
                      unit: item.unit_of_measure || item.unit || 'unit',
                      cost_per_unit: item.unit_cost || undefined,
                    })),
                    is_purchase: isPurchaseDocument,
                  })
                });

                if (movementResponse.ok) {
                  const movementData = await movementResponse.json();
                  if (movementData.success) {
                    console.log(`[DocumentModal] Created ${inventoryItems.length} inventory movements for ${doc_type} ${result.id}`);
                  }
                } else {
                  console.warn('[DocumentModal] Failed to create inventory movements:', await movementResponse.text());
                }
              }
            }
          } catch (error) {
            console.error('[DocumentModal] Error creating inventory movements:', error);
          }
        }

        // Create recurring schedule if "Create as Recurring" is enabled
        if (createAsRecurring && !doc?.id) {
          try {
            const token = authService.getTokens()?.access_token;
            if (token) {
              const recurringResponse = await fetch('/api/v1/finance/recurring', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  action: 'create',
                  name: `Recurring ${doc_type === 'invoice' ? 'Invoice' : 'Supplier Invoice'} - ${formData.contact_name || 'Customer'}`,
                  document_type: doc_type,
                  contact_id: formData.contact_id,
                  contact_name: formData.contact_name,
                  contact_email: formData.contact_email,
                  frequency: recurringSettings.frequency,
                  day_of_month: recurringSettings.day_of_month,
                  day_of_week: recurringSettings.day_of_week,
                  start_date: recurringSettings.start_date,
                  end_date: recurringSettings.end_date || null,
                  next_run_date: recurringSettings.start_date,
                  auto_send: recurringSettings.auto_send,
                  auto_approve: recurringSettings.auto_approve,
                  generate_days_before: recurringSettings.generate_days_before,
                  template_data: {
                    subtotal: formData.subtotal,
                    tax_total: formData.tax_total,
                    grand_total: formData.grand_total,
                    notes: formData.notes,
                    terms: formData.terms_and_conditions,
                    payment_terms: formData.payment_terms,
                    reference: formData.reference,
                  },
                  line_items: items.map(item => ({
                    item_name: item.description || item.item_name,
                    description: item.description,
                    notes: item.notes,
                    quantity: item.quantity,
                    unit: item.unit || 'each',
                    unit_cost: item.unit_price ?? item.unit_cost ?? item.rate ?? 0,
                    tax_rate: item.tax_rate || 0,
                    discount_amount: item.discount_amount || 0,
                    line_total: item.line_total ?? item.total_cost ?? item.amount ?? 0,
                    item_code: item.item_code,
                    is_optional: item.is_optional || false,
                    is_required: item.is_required || false,
                    quantity_editable: item.quantity_editable ?? true,
                    price_editable: item.price_editable ?? true,
                    source_bundle_id: item.source_bundle_id,
                    source_bundle_name: item.source_bundle_name,
                  })),
                })
              });

              if (recurringResponse.ok) {
                toast.success('Recurring schedule created successfully!', { icon: '🔄' });
              } else {
                const errorData = await recurringResponse.json();
                console.error('Error creating recurring schedule:', errorData);
                toast.error('Document saved, but failed to create recurring schedule');
              }
            }
          } catch (recurringError) {
            console.error('Error creating recurring schedule:', recurringError);
            toast.error('Document saved, but failed to create recurring schedule');
          }
        }

        // ── AI Learning loop: record vendor memory + per-line-item corrections ──
        // For expense documents (EXP/DIN), record both vendor-level and line-item-level
        // corrections so the AI learns from user adjustments.
        if (isPurchaseDocument && formData.contact_name) {
          const vendorName = formData.contact_name;
          const workItemId = result.id;

          // Record vendor-level mapping (overall document categorization)
          const overallAccountCode = formData.items?.[0]?.expense_account_code
            ?? formData.items?.[0]?.account_code
            ?? undefined;
          if (overallAccountCode) {
            recordVendorMemory({
              vendor_name: vendorName,
              account_code: overallAccountCode,
              work_item_id: workItemId,
            });
          }

          // Record per-line-item corrections
          for (const item of items) {
            const lineAccountCode = item.expense_account_code ?? item.account_code;
            const lineDescription = item.description || item.item_name;
            if (lineAccountCode && lineDescription) {
              recordVendorMemory({
                vendor_name: vendorName,
                account_code: lineAccountCode,
                line_item_description: lineDescription,
                work_item_id: workItemId,
              });
            }
          }
        }

        // §3.5.4 — Auto-trigger external bill update sync when a published
        // document is edited. Non-blocking: failures don't revert the local
        // save. Skipped when custom_fields.auto_update_external === false.
        const externalId = (result as { external_id?: string | null }).external_id
          || (result as { custom_fields?: { external_id?: string | null } }).custom_fields?.external_id;
        const autoUpdateDisabled = (result as { custom_fields?: { auto_update_external?: boolean } }).custom_fields?.auto_update_external === false;
        if (externalId && !autoUpdateDisabled && (doc?.id || formData.id)) {
          const token = authService.getTokens()?.access_token;
          if (token) {
            notify.info('Syncing changes to external system...');
            fetch('/api/v1/finance/automation/adapters/update-document', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ work_item_id: String(result.id) }),
            }).then((syncRes) => {
              if (syncRes.ok) {
                notify.success('Changes synced to external system');
              } else {
                notify.error('External sync failed — local save succeeded');
              }
            }).catch(() => {
              notify.error('External sync failed — local save succeeded');
            });
          }
        }

        if (openShareOnSave && !hasAttachmentSyncFailures) {
          setSavedDocument(result);
          setShowShareModal(true);
        } else {
          if (!skipOnSaved) {
            onSaved?.(result);
          }
          if (closeAfterSave && !hasAttachmentSyncFailures) {
            onClose();
          }
        }

        emitGuidedTourTrainingEvent({ type: 'document_create' });

        return result;
      }
    } catch (err) {
      console.error('Error saving document:', err);
      const apiErr = err as Error & { code?: string; details?: { contact_name?: string } };
      if (apiErr?.code === 'CREDIT_LIMIT_EXCEEDED') {
        const contactName = apiErr.details?.contact_name;
        notify.error(contactName ? `${contactName}: ${apiErr.message}` : apiErr.message || 'Credit limit exceeded');
      } else {
        toast.error('Failed to save document');
      }
      return null;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }

    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- doc and formData are intentionally spread/used as whole objects; individual fields are already listed
  }, [
    doc_type, doc?.id, formData.id, formData.contact_id, formData.contact_name, formData.contact_email,
    formData.status, formData.grand_total, formData.subtotal, formData.tax_total, formData.notes,
    formData.terms_and_conditions, formData.payment_terms, formData.reference, formData.issued_at, formData.transaction_date,
    formData.currency, formData.warehouse_id, formData.client_id, formData.parent_document_id,
    items, isPurchaseDocument, hasDocumentContact, ensureDocumentHasContentForAction,
    linkedParentDocument, importedWorkItemIds, importedTimeEntryIds,
    createAsRecurring, recurringSettings, syncPhotoAttachments,
    createDocument, updateDocument, markWorkItemsAsBilled,
    onClose, onSaved, setFormData, setHasUnsavedChanges, doc, formData,
  ]);

  // ============================================================================
  // SAVE HANDLER
  // ============================================================================

  const handleSave = useCallback(async (shouldSend: boolean = false) => {
    await persistDocument({ shouldSend, closeAfterSave: false });
  }, [persistDocument]);

  // ============================================================================
  // PRINT PREVIEW
  // ============================================================================

  const handleOpenPrintPreview = useCallback(async () => {
    if (!ensureDocumentHasContentForAction('preview')) {
      return;
    }

    const persistedPreviewDocument = doc
      || (formData.id ? ({
        id: String(formData.id),
        doc_type,
        doc_number: formData.doc_number || '',
        item_number: formData.item_number || undefined,
      } as Document) : null);

    if (!unifiedReportDocumentType) {
      setShowPrintPreview(true);
      return;
    }

    if (!persistedPreviewDocument?.id || hasUnsavedChanges) {
      setShowPrintPreview(true);
      return;
    }

    if (
      doc_type === 'invoice' &&
      invoiceUnifiedPreviewBlocked(buildInvoicePreviewLockProbe(doc ?? null, formData))
    ) {
      setShowPrintPreview(true);
      return;
    }

    setPreviewDocumentForUnified(persistedPreviewDocument);
    setShowUnifiedPreview(true);
  }, [ensureDocumentHasContentForAction, doc, formData, doc_type, unifiedReportDocumentType, hasUnsavedChanges]);

  // ============================================================================
  // DIN COMPLETION GATES
  // ============================================================================

  // Employee reimbursement claims (mileage, per-diem) are contactless by
  // design — the employee is the payee via assigned_to, not a supplier
  // contact. DINs carrying a reimbursement_route bypass the supplier gate;
  // every other gate (line items, GL, allocation) still applies.
  const isEmployeeReimbursementClaim =
    doc_type === 'din'
    && typeof doc?.custom_fields?.reimbursement_route === 'string';

  const validateDinCompletionGates = useCallback((): boolean => {
    if (!hasDocumentContact && !isEmployeeReimbursementClaim) {
      toast.error('A supplier must be allocated before completing this DIN. If the supplier does not exist yet, please create it first.');
      return false;
    }

    const meaningfulItems = items.filter((item) => {
      const lineType = item.line_type || 'item';
      if (lineType === 'spacer') return false;
      if (lineType === 'header' || lineType === 'comment') return false;
      if (lineType === 'subtotal') return false;
      return hasMeaningfulLineItemContent(item);
    });

    if (meaningfulItems.length === 0) {
      toast.error('At least one line item is required before completing this DIN.');
      return false;
    }

    const missingGL = meaningfulItems.filter((item) => !item.expense_account_code?.trim());
    if (missingGL.length > 0) {
      const firstDesc = missingGL[0]?.description?.trim() || `Line ${items.indexOf(missingGL[0]) + 1}`;
      toast.error(
        missingGL.length === 1
          ? `Line item "${firstDesc}" is missing a GL account. Please allocate a GL account before completing.`
          : `${missingGL.length} line items are missing a GL account. Please allocate GL accounts before completing.`
      );
      return false;
    }

    const missingAllocation = meaningfulItems.filter((item) => !item.allocation_type);
    if (missingAllocation.length > 0) {
      const firstDesc = missingAllocation[0]?.description?.trim() || `Line ${items.indexOf(missingAllocation[0]) + 1}`;
      toast.error(
        missingAllocation.length === 1
          ? `Line item "${firstDesc}" is missing an allocation type (Job, Stock, Overhead, etc.). Please select one before completing.`
          : `${missingAllocation.length} line items are missing an allocation type. Please select one for each before completing.`
      );
      return false;
    }

    // Customer + job is compulsory only on lines marked billable — QBO parity:
    // the Customer column is optional on expense lines unless the line is
    // billed back to a customer:job. A job-allocated but non-billable line may
    // keep or drop its link freely; links are never auto-removed.
    const missingBillableLink = meaningfulItems.filter((item) => {
      const gaps = getDinLineLinkGaps(item, { inventoryTrackingEnabled });
      return gaps.missingBillableCustomerJob;
    });
    if (missingBillableLink.length > 0) {
      const firstDesc = missingBillableLink[0]?.description?.trim() || `Line ${items.indexOf(missingBillableLink[0]) + 1}`;
      toast.error(
        missingBillableLink.length === 1
          ? `Line item "${firstDesc}" is marked billable but has no customer/job linked. Please link a customer and job, or uncheck Billable.`
          : `${missingBillableLink.length} line items are marked billable but have no customer/job linked. Please link them, or uncheck Billable.`
      );
      return false;
    }

    const missingStock = meaningfulItems.filter((item) => {
      const gaps = getDinLineLinkGaps(item, { inventoryTrackingEnabled });
      return gaps.missingStockItem;
    });
    if (missingStock.length > 0) {
      const firstDesc = missingStock[0]?.description?.trim() || `Line ${items.indexOf(missingStock[0]) + 1}`;
      toast.error(
        missingStock.length === 1
          ? `Line item "${firstDesc}" is allocated to Stock but no inventory item is linked. Please select an inventory item before completing.`
          : `${missingStock.length} line items are allocated to Stock but have no inventory item linked. Please link inventory items before completing.`
      );
      return false;
    }

    return true;
  }, [hasDocumentContact, isEmployeeReimbursementClaim, items, inventoryTrackingEnabled]);

  // ============================================================================
  // COMPLETE & PREVIEW
  // ============================================================================

  const handleCompleteAndPreview = useCallback(async () => {
    if (!ensureDocumentHasContentForAction('preview')) {
      return;
    }
    if (isPurchaseDocument && !hasDocumentContact && !isEmployeeReimbursementClaim) {
      toast.error('A supplier must be selected before you can preview this document.');
      return;
    }
    if (doc_type === 'din' && !validateDinCompletionGates()) {
      return;
    }
    setShowPromotionMenu(false);

    if (doc?.id && !hasUnsavedChanges) {
      if (unifiedReportDocumentType) {
        setPreviewDocumentForUnified(doc);
        setShowUnifiedPreview(true);
      } else {
        setShowPrintPreview(true);
      }
      return;
    }

    const saved = await persistDocument({
      shouldSend: false,
      closeAfterSave: false,
      openShareOnSave: false,
      skipOnSaved: true,
    });
    if (!saved?.id) {
      toast.error('Unable to prepare preview. Please save the document first.');
      return;
    }
    if (unifiedReportDocumentType) {
      setPreviewDocumentForUnified(saved);
      setShowUnifiedPreview(true);
    } else {
      setShowPrintPreview(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- doc is needed for setFinanceOverlayDocument but doc?.id is sufficient for the gate check
  }, [ensureDocumentHasContentForAction, isPurchaseDocument, hasDocumentContact, isEmployeeReimbursementClaim, doc_type, validateDinCompletionGates, doc?.id, hasUnsavedChanges, persistDocument, doc, unifiedReportDocumentType]);

  // ============================================================================
  // OPEN SEND MODAL
  // ============================================================================

  const handleOpenSendModal = useCallback(async () => {
    if (!ensureDocumentHasContentForAction('send')) {
      return;
    }
    if (isPurchaseDocument && !hasDocumentContact && !isEmployeeReimbursementClaim) {
      toast.error('A supplier must be selected before you can send this document.');
      return;
    }
    setShowPromotionMenu(false);
    let docToSend: Document | null = doc || null;
    if (!docToSend?.id || hasUnsavedChanges) {
      docToSend = await persistDocument({
        shouldSend: false,
        closeAfterSave: false,
        openShareOnSave: false,
        skipOnSaved: true,
      });
    }
    if (!docToSend?.id) {
      return;
    }
    setSavedDocument(docToSend);
    setShowShareModal(true);
  }, [ensureDocumentHasContentForAction, isPurchaseDocument, hasDocumentContact, isEmployeeReimbursementClaim, doc, hasUnsavedChanges, persistDocument]);

  // ============================================================================
  // SHARE LINK
  // ============================================================================

  const handleDocShareLinkAction = useCallback(async () => {
    const docId = doc?.id || formData.id;
    if (!docId || docShareUrlLoading) return;

    if (docShareUrl) {
      try {
        await navigator.clipboard.writeText(docShareUrl);
        notify.success('Share link copied to clipboard');
      } catch {
        notify.error('Failed to copy share link');
      }
      return;
    }

    setDocShareUrlLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        notify.error('Authentication required');
        return;
      }

      const res = await fetch('/api/v1/shares', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'create_link',
          document_id: docId,
          document_type: doc_type,
        }),
      });

      const data = await res.json();
      const shareData = data.share || data.data || data;
      const shareToken = shareData?.share_token || shareData?.token;
      const shareUrl = shareData?.share_url || (shareToken && typeof window !== 'undefined'
        ? `${window.location.origin}/view/${doc_type}/${shareToken}`
        : null);
      if (shareUrl) {
        setDocShareUrl(shareUrl);
        try {
          await navigator.clipboard.writeText(shareUrl);
          notify.success('Share link copied to clipboard');
        } catch {
          notify.error('Failed to copy share link');
        }
      } else {
        notify.error('Failed to generate share link');
      }
    } catch {
      notify.error('Failed to generate share link');
    } finally {
      setDocShareUrlLoading(false);
    }
  }, [doc?.id, formData.id, docShareUrl, docShareUrlLoading, doc_type]);

  // ============================================================================
  // DIN MATERIALIZE
  // ============================================================================

  type ConvertDocumentApiJson = {
    success?: boolean;
    error?: string;
    data?: {
      doc_number?: string;
      item_number?: string;
      produced_ref?: Record<string, unknown>;
    } & Record<string, unknown>;
  };

  const handleDinMaterialize = useCallback(async (opts?: { openShareOnComplete?: boolean }): Promise<Document | null> => {
    const sourceId = doc?.id || formData.id;
    if (!sourceId) {
      toast.error('Document ID not found');
      return null;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) throw new Error('Not authenticated');

    const editedPayload: Record<string, unknown> = {};
    if (formData.contact_id) editedPayload.linkage_contact_id = formData.contact_id;
    if (formData.notes) editedPayload.notes = formData.notes;
    if (formData.reference) editedPayload.reference = formData.reference;
    if (formData.issued_at) editedPayload.date = formData.issued_at;
    // Slip datetime — see the save_edit path above; needed here too because
    // materialize runs save_edit + materialize with the same payload.
    if (formData.transaction_date) editedPayload.transaction_date = formData.transaction_date;
    if (formData.currency) editedPayload.currency = formData.currency;
    if (formData.grand_total != null) {
      editedPayload.amount = roundMoney(formData.grand_total);
      editedPayload.amount_cents = toCents(formData.grand_total);
    }
    if (formData.contact_name) editedPayload.vendor_name = formData.contact_name;
    if (items.length > 0) editedPayload.line_items = items;

    // Generate a single idempotency key for the save_edit + materialize pair.
    // Both calls carry the same key so the server can detect duplicate retries
    // (e.g. double-click or network timeout + retry) and avoid double-materializing.
    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${sourceId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const saveRes = await fetch('/api/v1/finance/document-review', {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: sourceId,
        action: 'save_edit',
        edited_payload: editedPayload,
        matched_contact_id: formData.contact_id ?? undefined,
        idempotency_key: idempotencyKey,
      }),
    });
    if (!saveRes.ok) {
      const saveJson = await saveRes.json().catch(() => ({}));
      throw new Error(saveJson.error || 'Failed to save DIN edits');
    }
    const saveJson = await saveRes.json().catch(() => ({}));
    const dupCheck = saveJson?.data?.duplicateCheck;
    if (dupCheck?.hasDuplicates) {
      // Non-blocking: show a confirmation dialog so the user is aware of
      // potential duplicates, but let them proceed with promotion.
      // Duplicates should NOT gate promotion — only missing supplier,
      // missing GL account, or unbalanced line items should gate.
      const dupList = (dupCheck.duplicates as Array<{ item_number?: string; grand_total?: number; match_confidence?: string; status?: string }>).map(d =>
        `• ${d.item_number || 'Unknown'} — R${Number(d.grand_total || 0).toFixed(2)} (${d.match_confidence || 'match'}, ${d.status || 'unknown'})`
      ).join('\n');
      const proceed = await notify.confirm({
        title: 'Potential Duplicate Detected',
        message: `${dupCheck.duplicates.length} potential duplicate(s) detected for this supplier:\n\n${dupList}\n\nDo you want to proceed with promoting this document?`,
        confirmText: 'Proceed with promotion',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!proceed) {
        // User chose to cancel — edits are saved but materialization is skipped
        return null;
      }
    }

    const matRes = await fetch('/api/v1/finance/document-review', {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: sourceId, action: 'materialize', idempotency_key: idempotencyKey }),
    });

    const matRaw = await matRes.text();
    let matResult: ConvertDocumentApiJson = {};
    if (matRaw.trim()) {
      matResult = JSON.parse(matRaw) as ConvertDocumentApiJson;
    }

    if (!matRes.ok || !matResult.success) {
      throw new Error(matResult.error || 'Failed to materialize DIN');
    }

    const producedRef = matResult.data?.produced_ref;
    const expenseId = producedRef
      ? (producedRef as Record<string, unknown>).expense_id as string || (producedRef as Record<string, unknown>).finance_document_id as string
      : undefined;

    if (!expenseId) {
      throw new Error('Materialize succeeded but no expense ID returned');
    }

    const docRes = await fetch(`/api/v1/work-items/documents/${expenseId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!docRes.ok) {
      throw new Error('Materialize succeeded but failed to fetch the new expense document');
    }
    const docJson = await docRes.json();
    const newDoc = docJson.data as unknown as Document;

    toast.success(`Expense created! ${newDoc?.doc_number || newDoc?.item_number || ''}`);

    // ── AI Learning loop: record vendor memory on DIN materialize ──
    // Materialization is the final confirmation of the DIN review — the user
    // has chosen the supplier and GL allocation and clicked "Create Expense".
    // This is the strongest learning signal, so we record it here too.
    if (formData.contact_name) {
      const vendorName = formData.contact_name;
      const workItemId = String(expenseId || sourceId);

      // Record supplier mapping
      if (formData.contact_id) {
        recordVendorMemory({
          vendor_name: vendorName,
          contact_id: formData.contact_id,
          contact_name: formData.contact_name,
          work_item_id: workItemId,
        });
      }

      // Record vendor-level GL mapping
      const overallAccountCode = formData.items?.[0]?.expense_account_code
        ?? formData.items?.[0]?.account_code
        ?? items?.[0]?.expense_account_code
        ?? items?.[0]?.account_code
        ?? undefined;
      if (overallAccountCode) {
        recordVendorMemory({
          vendor_name: vendorName,
          account_code: overallAccountCode,
          work_item_id: workItemId,
        });
      }

      // Record per-line-item GL corrections
      for (const item of items) {
        const lineAccountCode = item.expense_account_code ?? item.account_code;
        const lineDescription = item.description || item.item_name;
        if (lineAccountCode && lineDescription) {
          recordVendorMemory({
            vendor_name: vendorName,
            account_code: lineAccountCode,
            line_item_description: lineDescription,
            work_item_id: workItemId,
          });
        }
      }
    }

    onConverted?.({ success: true, document: newDoc as unknown as Record<string, unknown>, targetType: 'expense' });

    if (opts?.openShareOnComplete && newDoc?.id) {
      setSavedDocument(newDoc);
      setShowShareModal(true);
      onSaved?.(newDoc);
    } else {
      // Just close the DIN modal — the DIN is now 'completed' and the
      // materialized expense is visible in FinanceDocumentView.  We do NOT
      // dispatch openDocumentModal here because the "Create Expense" button
      // is the final action; opening the expense in a new modal would be an
      // unwanted extra step.
      onClose();
    }

    return newDoc;
  }, [doc?.id, formData.id, formData.contact_id, formData.notes, formData.reference, formData.issued_at, formData.transaction_date, formData.currency, formData.grand_total, formData.contact_name, formData.items, items, onConverted, onSaved, onClose]);

  // ============================================================================
  // PROMOTE DOCUMENT
  // ============================================================================

  const handlePromoteDocument = useCallback(async (targetType: DocumentType) => {
    if (isPromoting || isPromotingRef.current) return;
    isPromotingRef.current = true;

    if (doc_type === 'din') {
      if (!validateDinCompletionGates()) return;
      setIsPromoting(true);
      setShowPromotionMenu(false);
      try {
        await handleDinMaterialize({ openShareOnComplete: false });
      } catch (err: unknown) {
        console.error('Error materializing DIN:', err);
        toast.error(getErrorMessage(err, 'Failed to materialize DIN'));
      } finally {
        setIsPromoting(false);
        isPromotingRef.current = false;
      }
      return;
    }

    if (!isFinanceDocumentTypeAllowed(targetType, financeTierRestrictions)) {
      toast.error('Your current finance package does not include that downstream document type.');
      return;
    }

    let sourceId = doc?.id || formData.id;
    if (!sourceId || hasUnsavedChanges) {
      const saved = await persistDocument({
        shouldSend: false,
        closeAfterSave: false,
        openShareOnSave: false,
      });
      sourceId = saved?.id ?? doc?.id ?? formData.id;
    }

    if (!sourceId) {
      toast.error('Please save the document first before converting');
      return;
    }

    setIsPromoting(true);
    setShowPromotionMenu(false);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/v1/finance', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'convertDocument',
          sourceDocumentId: sourceId,
          targetDocType: targetType,
          copyItems: true,
          ...(targetType === 'credit_note' || targetType === 'supplier_credit'
            ? { makeNegative: true }
            : {}
          ),
        }),
      });

      const rawBody = await response.text();
      let result: ConvertDocumentApiJson = {};
      if (rawBody.trim()) {
        try {
          result = JSON.parse(rawBody) as ConvertDocumentApiJson;
        } catch {
          throw new Error(
            `Could not read server response (${response.status}). The finance API may be unreachable or returned non-JSON.`,
          );
        }
      } else if (!response.ok) {
        const hint =
          response.status === 502
            ? 'Bad gateway — upstream server or proxy error.'
            : response.status >= 500
              ? 'Server error — check API logs for handleConvertDocument.'
              : '';
        throw new Error(
          hint
            ? `Request failed (${response.status}). ${hint}`
            : `Request failed (${response.status}) with an empty response body.`,
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Failed to convert document (${response.status})`);
      }

      const targetLabels: Record<string, string> = {
        quote: 'Quote',
        proforma: 'Proforma',
        invoice: 'Invoice',
        sales_order: 'Sales Order',
        work_order: 'Work Order',
        purchase_order: 'Purchase Order',
        expense: 'Expense',
        grn: 'GRN',
        delivery_note: 'Delivery Note',
        credit_note: 'Credit Note',
        supplier_credit: 'Supplier Credit',
      };

      toast.success(`Document converted to ${targetLabels[targetType] || targetType}! ${result.data?.doc_number || result.data?.item_number || ''}`);

      onConverted?.({ success: true, document: result.data ?? {}, targetType });
      onClose();

      // Fetch the full converted document so the opened modal has complete
      // data (line items, contact info, etc.) rather than the partial payload
      // returned by the convert API. Falls back to the partial data on error.
      if (typeof window !== 'undefined' && result.data) {
        const newDocId = (result.data as Record<string, unknown>).id as string | undefined;
        const partialDoc = result.data as unknown as Document;
        (async () => {
          try {
            if (newDocId) {
              const docRes = await fetch(`/api/v1/work-items/documents/${newDocId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (docRes.ok) {
                const docJson = await docRes.json();
                if (docJson.success && docJson.data) {
                  window.dispatchEvent(
                    new CustomEvent('openDocumentModal', {
                      detail: {
                        doc_type: targetType,
                        mode: 'edit',
                        document: docJson.data,
                      },
                    }),
                  );
                  return;
                }
              }
            }
          } catch {
            // Fall through to partial fallback below
          }
          // Fallback: dispatch with the partial data from the convert response
          window.dispatchEvent(
            new CustomEvent('openDocumentModal', {
              detail: {
                doc_type: targetType,
                mode: 'edit',
                document: partialDoc,
              },
            }),
          );
        })();
      }

    } catch (err: unknown) {
      console.error('Error promoting document:', err);
      toast.error(getErrorMessage(err, 'Failed to convert document'));
    } finally {
      setIsPromoting(false);
    }
  }, [isPromoting, doc_type, validateDinCompletionGates, handleDinMaterialize, financeTierRestrictions, doc?.id, formData.id, hasUnsavedChanges, persistDocument, onConverted, onClose]);

  // ============================================================================
  // COMPLETE & SEND CONVERSION
  // ============================================================================

  const handleCompleteAndSendConversion = useCallback(async () => {
    if (isPromoting || isPromotingRef.current) return;
    isPromotingRef.current = true;

    if (!ensureDocumentHasContentForAction('send')) return;

    if (isPurchaseDocument && !hasDocumentContact && !isEmployeeReimbursementClaim) {
      toast.error('A supplier must be selected before you can send this document.');
      return;
    }

    setShowPromotionMenu(false);

    if (doc_type === 'din') {
      if (!validateDinCompletionGates()) return;
      setIsPromoting(true);
      try {
        await handleDinMaterialize({ openShareOnComplete: true });
      } catch (err: unknown) {
        console.error('Error materializing DIN for send:', err);
        toast.error(getErrorMessage(err, 'Failed to materialize DIN'));
      } finally {
        setIsPromoting(false);
        isPromotingRef.current = false;
      }
      return;
    }

    let sourceId = doc?.id || formData.id;
    if (!sourceId || hasUnsavedChanges) {
      const saved = await persistDocument({
        shouldSend: false,
        closeAfterSave: false,
        openShareOnSave: false,
        skipOnSaved: true,
      });
      sourceId = saved?.id ?? doc?.id ?? formData.id;
    }

    if (!sourceId) {
      toast.error('Please save the document first before converting');
      return;
    }

    setIsPromoting(true);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/v1/finance', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'convertDocument',
          sourceDocumentId: sourceId,
          targetDocType: 'expense',
          copyItems: true,
        }),
      });

      const rawBody = await response.text();
      let result: ConvertDocumentApiJson = {};
      if (rawBody.trim()) {
        try {
          result = JSON.parse(rawBody) as ConvertDocumentApiJson;
        } catch {
          throw new Error('Could not read server response. The finance API may be unreachable.');
        }
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Failed to convert document (${response.status})`);
      }

      toast.success(`Expense created! ${result.data?.doc_number || result.data?.item_number || ''}`);

      onConverted?.({ success: true, document: result.data ?? {}, targetType: 'expense' });

      // Mark the source document as converted so the UI reflects that it has
      // been consumed. This prevents the user from closing the share modal and
      // attempting a duplicate conversion (canPromote is gated on status).
      setFormData((prev) => ({ ...prev, status: 'converted' as DocumentStatus }));

      const newDoc = result.data as unknown as Document;
      if (newDoc?.id) {
        setSavedDocument(newDoc);
        setShowShareModal(true);
        onSaved?.(newDoc);
      }
    } catch (err: unknown) {
      console.error('Error converting document for send:', err);
      toast.error(getErrorMessage(err, 'Failed to convert document'));
    } finally {
      setIsPromoting(false);
    }
  }, [isPromoting, ensureDocumentHasContentForAction, isPurchaseDocument, hasDocumentContact, isEmployeeReimbursementClaim, doc_type, validateDinCompletionGates, handleDinMaterialize, doc?.id, formData.id, hasUnsavedChanges, persistDocument, onConverted, onSaved, setFormData]);

  // ============================================================================
  // APPROVE & PUBLISH (standard mode — DIN → expense → approve → auto-publish)
  // ============================================================================

  const handleApproveAndPublish = useCallback(async (): Promise<void> => {
    if (isPromoting || isPromotingRef.current) return;
    isPromotingRef.current = true;
    if (doc_type !== 'din') return;
    if (!validateDinCompletionGates()) return;

    setIsPromoting(true);
    setShowPromotionMenu(false);
    try {
      // 1. Materialize the DIN → creates the expense/supplier_invoice document.
      //    In standard/simplified mode the materialize endpoint creates the
      //    expense with 'approved' status and already calls publishOnApproval,
      //    so the explicit approve step below is only needed for strict mode
      //    (where the expense starts as 'pending_approval').
      const newDoc = await handleDinMaterialize({ openShareOnComplete: false });
      if (!newDoc?.id) {
        toast.error('Failed to create document from DIN');
        return;
      }

      const docLabel = newDoc.doc_number || newDoc.item_number || newDoc.id;
      const producedStatus = String(newDoc.status || '').toLowerCase();
      // 'approved' — standard/simplified mode approves on materialize.
      // 'paid' / 'published' — COD suppliers (payment_terms_days=0) produce an
      // already-paid expense; the lifecycle has no 'approve' transition from
      // 'paid', so attempting one returns a 409.  Both are terminal states
      // where materialize already attempted the publish.
      const isTerminal = ['approved', 'paid', 'published'].includes(producedStatus);

      if (isTerminal) {
        toast.success(producedStatus === 'paid'
          ? `Created ${docLabel} — paid at capture`
          : `Approved & published ${docLabel}`);
        onConverted?.({ success: true, document: newDoc as unknown as Record<string, unknown>, targetType: 'expense' });
        onClose();
        return;
      }

      // 2. Strict mode — approve the newly created document via the lifecycle
      //    action API.  The server's handlePerformDocumentAction stamps
      //    approved_by/approved_at and calls publishOnApproval() which
      //    auto-publishes to the connected external adapter (QBO/Xero/Sage)
      //    when publish_trigger is auto_publish.
      const token = authService.getTokens()?.access_token;
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const approveRes = await fetch('/api/v1/finance', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'performDocumentAction',
          data: { id: String(newDoc.id), action_id: 'approve' },
        }),
      });

      const approveJson = await approveRes.json().catch(() => ({}));
      if (!approveRes.ok || !approveJson.success) {
        throw new Error(approveJson.error || 'Failed to approve document');
      }

      // 3. Check if the server auto-published
      const externalPublish = approveJson.data?.external_publish;
      if (externalPublish?.published) {
        toast.success(`Approved & published ${docLabel} to ${externalPublish.adapterId || 'external system'}`);
      } else if (externalPublish?.error) {
        toast.success(`Approved ${docLabel} — publish deferred: ${externalPublish.error}`);
      } else {
        toast.success(`Approved ${docLabel}`);
      }

      onConverted?.({ success: true, document: newDoc as unknown as Record<string, unknown>, targetType: 'expense' });
      onClose();
    } catch (err: unknown) {
      console.error('Error approving & publishing DIN:', err);
      toast.error(getErrorMessage(err, 'Failed to approve & publish'));
    } finally {
      setIsPromoting(false);
      isPromotingRef.current = false;
    }
  }, [isPromoting, doc_type, validateDinCompletionGates, handleDinMaterialize, onConverted, onClose]);

  // ============================================================================
  // RESET
  // ============================================================================

  const resetLifecycleState = useCallback(() => {
    setIsSaving(false);
    isSavingRef.current = false;
    setAutoSaveEnabled(true);
    setAutoSaveStatus('idle');
    setLastAutoSave(null);
    setHasUnsavedChanges(false);
    hasUnsavedChangesRef.current = false;
    setShowShareModal(false);
    setSavedDocument(null);
    setDocShareUrl(null);
    setDocShareUrlLoading(false);
    setShowPromotionMenu(false);
    setIsPromoting(false);
    setShowPrintPreview(false);
    setShowUnifiedPreview(false);
    setPreviewDocumentForUnified(null);
    setCreateAsRecurring(false);
    setRecurringSettings({
      frequency: 'monthly',
      start_date: getDateStrSAST(new Date()),
      end_date: '',
      day_of_month: 1,
      day_of_week: 1,
      auto_send: false,
      auto_approve: false,
      generate_days_before: 7,
    });
  }, [setHasUnsavedChanges]);

  return {
    isSaving,
    autoSaveEnabled,
    setAutoSaveEnabled,
    autoSaveStatus,
    lastAutoSave,
    showShareModal,
    setShowShareModal,
    savedDocument,
    setSavedDocument,
    docShareUrl,
    docShareUrlLoading,
    showPromotionMenu,
    setShowPromotionMenu,
    isPromoting,
    isSavingRef,
    showPrintPreview,
    setShowPrintPreview,
    showUnifiedPreview,
    setShowUnifiedPreview,
    previewDocumentForUnified,
    setPreviewDocumentForUnified,
    createAsRecurring,
    setCreateAsRecurring,
    recurringSettings,
    setRecurringSettings,
    handleCloseModal,
    ensureDocumentHasContentForAction,
    persistDocument,
    handleSave,
    handleOpenPrintPreview,
    validateDinCompletionGates,
    handleCompleteAndPreview,
    handleOpenSendModal,
    handleDocShareLinkAction,
    handleDinMaterialize,
    handlePromoteDocument,
    handleCompleteAndSendConversion,
    handleApproveAndPublish,
    markWorkItemsAsBilled,
    resetLifecycleState,
  };
}
